import { AccountBalance, ChevronRight, Groups, OpenInNew } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import DownloadFile from "components/DownloadFile";
import { RefreshData } from "components/helper";
import VisaDocumentTemplateManagerDialog from "components/Visa/VisaDocumentTemplateManagerDialog";
import { P } from "const/permissions";
import DisplayDate from "components/DisplayDate";
import { usePermission } from "hooks/usePermissions";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { getMrtBodyRowSx, getMrtTableThemeProps } from "theme/mrtTableTheme";
import { getThemedTabsSx } from "theme/tabsTheme";

const DEFAULT_QUERY = {
  pageIndex: 0,
  pageSize: 20,
  search: "",
  status: "All",
  type: "All",
  paymentStatus: null,
};

export default function ModuleClientsIndex({ config }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [tabIndex, setTabIndex] = useState(0);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const canReadDocumentTemplates = usePermission(P.VISA_DOCUMENT.READ);
  const canCreateDocumentTemplates = usePermission(P.VISA_DOCUMENT.CREATE);
  const canViewDocumentTemplates = canReadDocumentTemplates || canCreateDocumentTemplates;

  const listTabs = config.listTabs;
  const tabParamKey = config.listTabParamKey;

  useEffect(() => {
    setQuery((prev) => ({ ...prev, pageIndex: 0 }));
  }, [query.search, query.status, query.type, query.paymentStatus]);

  const clientsQuery = useQuery(
    [config.clientsQueryKey, query, config.key],
    () => config.fetchClientsPage(query),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const pageData = clientsQuery.data ?? { content: [], totalItems: 0 };
  const clients = pageData.content ?? [];
  const totalItems = pageData.totalItems ?? 0;
  const listLoading = clientsQuery.isFetching;

  const openClient = (client) => {
    navigate(`${config.basePath}/client/${client.clientId}`);
  };

  const handleTabChange = (_event, newValue) => {
    setTabIndex(newValue);
    if (!listTabs?.length) return;
    const tabValue = listTabs[newValue];
    if (tabParamKey === "type") {
      setQuery((prev) => ({ ...prev, type: tabValue, pageIndex: 0 }));
    } else if (tabParamKey === "status") {
      setQuery((prev) => ({ ...prev, status: tabValue, pageIndex: 0 }));
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Client",
        size: 240,
        Cell: ({ row }) => {
          const client = row.original;
          return (
            <div className="min-w-0">
              <div className="font-medium text-app-text truncate">
                {client.passportNumber
                  ? `${client.name} · ${client.passportNumber}`
                  : client.name || "—"}
              </div>
              {client.reference && (
                <div className="text-xs text-app-text-muted truncate">Ref: {client.reference}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "number",
        header: "Phone",
        size: 140,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "entryCount",
        header: config.entryCountLabel,
        size: 120,
        Cell: ({ cell }) => (
          <Chip size="small" color="primary" variant="outlined" label={cell.getValue() ?? 0} />
        ),
      },
      {
        accessorKey: "caseOfficer",
        header: "Case Officer",
        size: 150,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "latestActivityDate",
        header: "Last activity",
        size: 140,
        Cell: ({ cell }) => <DisplayDate value={cell.getValue()} className="text-sm" />,
      },
      {
        id: "actions",
        header: "Actions",
        size: 90,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <Tooltip title={`Open client ${config.entriesSectionLabel.toLowerCase()}`}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openClient(row.original);
                }}
              >
                <ChevronRight />
              </IconButton>
            </Tooltip>
            <Tooltip title="Client profile">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/client/view/${row.original.clientId}`);
                }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Direct account">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/admin/account/add", {
                    state: {
                      item: {
                        clientData: {
                          id: row.original.clientId,
                          name: row.original.name,
                          email: row.original.email,
                          number: row.original.number,
                          caseOfficer: row.original.caseOfficer,
                        },
                      },
                    },
                  });
                }}
              >
                <AccountBalance fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [config, navigate]
  );

  const table = useMaterialReactTable({
    columns,
    data: clients,
    getRowId: (row) => String(row.clientId),
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableStickyHeader: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalItems,
    pageCount: Math.ceil(totalItems / query.pageSize) || 0,
    onPaginationChange: (updater) => {
      setQuery((prev) => {
        const pagination =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, ...pagination };
      });
    },
    onGlobalFilterChange: (value) => {
      setQuery((prev) => ({ ...prev, search: value, pageIndex: 0 }));
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        left: ["mrt-row-numbers", "name"],
        right: ["actions"],
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50, 100],
    },
    paginationDisplayMode: "pages",
    state: {
      isLoading: listLoading,
      showProgressBars: listLoading,
      globalFilter: query.search || "",
      pagination: { pageIndex: query.pageIndex, pageSize: query.pageSize },
    },
    ...getMrtTableThemeProps({
      maxHeight: listTabs?.length ? "calc(100vh - 380px)" : "calc(100vh - 320px)",
    }),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest("button");
        if (!isActionClick) openClient(row.original);
      },
      sx: getMrtBodyRowSx(row),
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2 text-app-text">
                  <Groups fontSize="small" />
                  {config.title} ({totalItems})
                  <RefreshData refreshData={clientsQuery.refetch} loading={listLoading} />
                </h3>
                <p className="text-sm text-app-text-muted mt-0.5">{config.subtitle}</p>
              </div>
              <div className="flex items-center gap-3">
                {totalItems > 0 && config.downloadType && (
                  <DownloadFile type={config.downloadType} />
                )}
                {/*
                {config.hasDocumentTemplates && canViewDocumentTemplates && (
                  <Button
                    variant="outlined"
                    startIcon={<DescriptionOutlinedIcon fontSize="small" />}
                    onClick={() => setTemplateManagerOpen(true)}
                  >
                    Manage document templates
                  </Button>
                )}
                */}
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  to={config.basePath + "/add"}
                >
                  {config.addLabel}
                </Button>
              </div>
            </div>
          </div>

          {listTabs?.length > 0 && (
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              sx={getThemedTabsSx()}
            >
              {listTabs.map((tab) => (
                <Tab
                  key={tab}
                  label={config.listTabLabels?.[tab] ?? tab}
                  sx={{ fontWeight: "bold", fontSize: 12 }}
                />
              ))}
            </Tabs>
          )}

          <div className="px-4 pb-4">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>

      {config.hasDocumentTemplates && (
        <VisaDocumentTemplateManagerDialog
          open={templateManagerOpen}
          onClose={() => setTemplateManagerOpen(false)}
        />
      )}
    </div>
  );
}
